import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Divider,
  Grid,
  Modal,
  Slide,
  useMediaQuery,
} from '@mui/material';
import { H2, H7, Small, Tiny } from 'components/Typography';
import FlexBox from 'components/flexbox/FlexBox';
import FlexRowAlign from 'components/flexbox/FlexRowAlign';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { X } from 'react-feather';
import { useNavigate } from 'react-router-dom';

const ModalContent = ({
  handleClose,
  loan,
  setLoan,
  setShowModal,
  isTablet,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [firstOption, setFirstOption] = useState(false);

  return (
    <>
      <FlexBox
        mb={3}
        p={1}
        container
        sx={{
          borderRadius: 3,
          justifyContent: 'flex-start',
          position: 'relative',
          flexDirection: 'column',
        }}
      >
        <H2
          sx={{
            fontSize: isTablet ? '22px' : '25px',
            textAlign: 'left',
          }}
        >
          골드회원권
        </H2>

        <Box sx={{ position: 'absolute', right: 0, cursor: 'pointer' }}>
          <X
            size={20}
            onClick={handleClose}
            color={theme.palette.primary.grey400}
          />
        </Box>
      </FlexBox>

      <Grid container px={1} gap={1} sx={{ overflow: 'hidden' }}>
        <Grid
          container
          item
          xs={12}
          alignItems="center"
          justifyContent="space-between"
        >
          <H7 sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            가격
          </H7>
          <H7 sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            600,000원
          </H7>
        </Grid>
        <Grid
          container
          item
          xs={12}
          alignItems="center"
          justifyContent="space-between"
        >
          <H7 sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            VAT
          </H7>
          <H7 sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            60,000원
          </H7>
        </Grid>

        <Divider sx={{ width: '100%', my: 1.5 }} />

        <Grid
          mb={1.5}
          container
          item
          xs={12}
          alignItems="center"
          justifyContent="space-between"
        >
          <H7 sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            주문금액 합계
          </H7>
          <H7 sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            660,000원
          </H7>
        </Grid>

        <Small sx={{ width: '100%' }}>구매시 유의사항</Small>

        <Small>
          <li>이용권은 구매일로부터 등업이 완료된 시점부터 사용이 됩니다</li>
        </Small>
        <Small>
          <li>모든 이용권은 부가세(VAT) 포함 가격입니다.</li>
        </Small>
        <Small>
          <li>
            결제 후 다음날부터 14일 이내 서비스를 사용하지 않은 경우에 한하여
            금액을 전액 환불해 드립니다.
          </li>
        </Small>
        <Small>
          <li>
            단, 지식재산권에 의해 정닥터 닷컴에서 등업이 된 경우 서비스를 사용한
            것으로 간주하여 환불이 제한됩니다.
          </li>
        </Small>

        <Grid
          mt={4}
          item
          xs={12}
          justifyContent="center"
          alignItems="center"
          display="flex"
          flexDirection="column"
        >
          <FlexRowAlign
            onClick={() => setFirstOption(!firstOption)}
            gap={0}
            mb={1}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <FlexBox
              sx={{
                width: '30px',
                height: '30px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Check
                color={
                  firstOption
                    ? theme.palette.primary.darkBlue
                    : theme.palette.primary.grey400
                }
                cursor="pointer"
                size={20}
                strokeWidth={2}
              />
            </FlexBox>

            <Tiny
              sx={{
                color: firstOption
                  ? theme.palette.primary.darkBlue
                  : theme.palette.primary.grey400,
              }}
            >
              상기 회원권 환불조건을 모두 확인하였습니다.
            </Tiny>
          </FlexRowAlign>
          <Button
            onClick={() => {
              setShowModal(false);
            }}
            sx={{
              width: '100%',
              height: '50px',
              borderRadius: '12px',
              background: '#343434',
              color: theme.palette.primary.white,
              '&:hover': {
                background: '#343434',
              },
            }}
          >
            결제하기
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default function MembershipPurchaseModal({ showModal, setShowModal }) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loan, setLoan] = useState('');

  const style = {
    position: isTablet ? 'absolute' : 'relative',
    left: isTablet ? '0' : '50%',
    top: isTablet ? 'auto' : '50%',
    bottom: isTablet ? 0 : 'auto',
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24,
    borderBottomRightRadius: isTablet ? 0 : 24,
    borderBottomLeftRadius: isTablet ? 0 : 24,
    transform: isTablet ? 'none' : 'translate(-50%, -50%)',
    width: isTablet ? '100%' : '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    border: '1px solid #E1E1E1',
    p: isTablet ? 2 : 4,
    py: isTablet ? 4 : 5,
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '0.4em',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
    },
    outline: 'none',
    '& *': {
      overflow: 'visible',
    },
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <Modal
      open={showModal}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      style={{
        backdropFilter: 'blur(5px)',
      }}
    >
      {isTablet ? (
        <Slide direction="up" in={showModal} mountOnEnter unmountOnExit>
          <Box sx={style}>
            <ModalContent
              handleClose={handleClose}
              loan={loan}
              setLoan={setLoan}
              setShowModal={setShowModal}
            />
          </Box>
        </Slide>
      ) : (
        <Box sx={style}>
          <ModalContent
            handleClose={handleClose}
            loan={loan}
            setLoan={setLoan}
            setShowModal={setShowModal}
          />
        </Box>
      )}
    </Modal>
  );
}
